/* eslint-disable @stylistic/max-statements-per-line */
import { ColourMatrixUniformData } from '../pipeline-colourMatrix';

import { MatrixTuple } from './pipeline-utils-types';

export type ColourMatrixTuple = [
  ...MatrixTuple,
  number, number, number, number
];

export enum ColourMatrixProp {
  brightness = 'brightness',
  saturation = 'saturation',
  desaturation = 'desaturation',
  hueShift = 'hueShift',
  contrast = 'contrast',
  dayNight = 'dayNight',
  blackWhite = 'blackWhite',
  negative = 'negative',
  desaturateLuminance = 'desaturateLuminance',
  sepia = 'sepia',
  lsd = 'LSD',
  brown = 'brown',
  vintagePinhole = 'vintagePinhole',
  kodachrome = 'kodachrome',
  technicolor = 'technicolor',
  polaroid = 'polaroid',
  BGR = 'BGR'
}

/**
 * TODO: Leave notes
 */
export interface ColourMatrixPropEntry {
  prop: ColourMatrixProp;
  value: number | boolean;
}
export type ColourMatrixPropUsage = ColourMatrixPropEntry[];

/**
 * TODO: Leave notes
 */

export function ConvertToColourMatrixUniformData(colourMatrixPropUsage: ColourMatrixPropUsage): ColourMatrixUniformData {
  const base: ColourMatrixTuple = [
    1, 0, 0, 0, 0,
    0, 1, 0, 0, 0,
    0, 0, 1, 0, 0,
    0, 0, 0, 1, 0
  ];

  for (const entry of colourMatrixPropUsage) {
    const UtilizeFunc = colourMatrixPropCalls[entry.prop];

    UtilizeFunc && UtilizeFunc(base, entry.value);
  }

  // TODO: Add a slider to wim-Program and extra prop into colour-matrix pipelines, for this alpha value.
  // * It would determine to exact level of the usage of the changes, 0 being just the base image again, 1 being 100% modification.
  // * Not yet requested, low priority.
  return {
    matrix_4x5: GetData(base),
    alpha: 1.0 // So worthless right now :/
  };
}

export function ArrayCopy(original: ColourMatrixTuple): number[] {
  const copy: number[] = [];

  for (let i = 0; i < original.length; i++) {
    copy[i] = original[i];
  }

  return copy;
}

export function GetData(base: ColourMatrixTuple): Float32Array {
  const f32Array = new Float32Array(base);

  f32Array[4] /= 255;
  f32Array[9] /= 255;
  f32Array[14] /= 255;
  f32Array[19] /= 255;

  return f32Array;
}

export function Reset(base: ColourMatrixTuple) {
  //  Long-winded, but saves on gc, which happens a lot in Post FX Shaders
  //  that reset the ColorMatrix every frame.
  base[0] = 1; base[1] = 0; base[2] = 0; base[3] = 0; base[4] = 0;
  base[5] = 0; base[6] = 1; base[7] = 0; base[8] = 0; base[9] = 0;
  base[10] = 0; base[11] = 0; base[12] = 1; base[13] = 0; base[14] = 0;
  base[15] = 0; base[16] = 0; base[17] = 0; base[18] = 1; base[19] = 0;
}

export function Multiply(effect: ColourMatrixTuple, base: ColourMatrixTuple) {
  const copy = ArrayCopy(base);

  //  R
  base[0] = (copy[0] * effect[0]) + (copy[1] * effect[5]) + (copy[2] * effect[10]) + (copy[3] * effect[15]);
  base[1] = (copy[0] * effect[1]) + (copy[1] * effect[6]) + (copy[2] * effect[11]) + (copy[3] * effect[16]);
  base[2] = (copy[0] * effect[2]) + (copy[1] * effect[7]) + (copy[2] * effect[12]) + (copy[3] * effect[17]);
  base[3] = (copy[0] * effect[3]) + (copy[1] * effect[8]) + (copy[2] * effect[13]) + (copy[3] * effect[18]);
  base[4] = (copy[0] * effect[4]) + (copy[1] * effect[9]) + (copy[2] * effect[14]) + (copy[3] * effect[19]) + copy[4];

  //  G
  base[5] = (copy[5] * effect[0]) + (copy[6] * effect[5]) + (copy[7] * effect[10]) + (copy[8] * effect[15]);
  base[6] = (copy[5] * effect[1]) + (copy[6] * effect[6]) + (copy[7] * effect[11]) + (copy[8] * effect[16]);
  base[7] = (copy[5] * effect[2]) + (copy[6] * effect[7]) + (copy[7] * effect[12]) + (copy[8] * effect[17]);
  base[8] = (copy[5] * effect[3]) + (copy[6] * effect[8]) + (copy[7] * effect[13]) + (copy[8] * effect[18]);
  base[9] = (copy[5] * effect[4]) + (copy[6] * effect[9]) + (copy[7] * effect[14]) + (copy[8] * effect[19]) + copy[9];

  //  B
  base[10] = (copy[10] * effect[0]) + (copy[11] * effect[5]) + (copy[12] * effect[10]) + (copy[13] * effect[15]);
  base[11] = (copy[10] * effect[1]) + (copy[11] * effect[6]) + (copy[12] * effect[11]) + (copy[13] * effect[16]);
  base[12] = (copy[10] * effect[2]) + (copy[11] * effect[7]) + (copy[12] * effect[12]) + (copy[13] * effect[17]);
  base[13] = (copy[10] * effect[3]) + (copy[11] * effect[8]) + (copy[12] * effect[13]) + (copy[13] * effect[18]);
  base[14] = (copy[10] * effect[4]) + (copy[11] * effect[9]) + (copy[12] * effect[14]) + (copy[13] * effect[19]) + copy[14];

  //  A
  base[15] = (copy[15] * effect[0]) + (copy[16] * effect[5]) + (copy[17] * effect[10]) + (copy[18] * effect[15]);
  base[16] = (copy[15] * effect[1]) + (copy[16] * effect[6]) + (copy[17] * effect[11]) + (copy[18] * effect[16]);
  base[17] = (copy[15] * effect[2]) + (copy[16] * effect[7]) + (copy[17] * effect[12]) + (copy[18] * effect[17]);
  base[18] = (copy[15] * effect[3]) + (copy[16] * effect[8]) + (copy[17] * effect[13]) + (copy[18] * effect[18]);
  base[19] = (copy[15] * effect[4]) + (copy[16] * effect[9]) + (copy[17] * effect[14]) + (copy[18] * effect[19]) + copy[19];
}

// TODO: define value limitations
// ! And impose in wim-program
export function Brightness(base: ColourMatrixTuple, value: number) {
  Multiply([
    value, 0, 0, 0, 0,
    0, value, 0, 0, 0,
    0, 0, value, 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

// TODO: define value limitations
// ! And impose in wim-program
export function Saturation(base: ColourMatrixTuple, value: number) {
  const x = (value * 2 / 3) + 1;
  const y = ((x - 1) * -0.5);

  Multiply([
    x, y, y, 0, 0,
    y, x, y, 0, 0,
    y, y, x, 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

// TODO: define value limitations
// ! And impose in wim-program - This will be pre-limited to use 0 to -1 saturation
export function Desaturation(base: ColourMatrixTuple, value: number) {
  Saturation(base, -value);
}

// TODO: define value limitations
// ! And impose in wim-program
export function HueShift(base: ColourMatrixTuple, rotation: number) {
  rotation = rotation / 180 * Math.PI;

  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const lumR = 0.213;
  const lumG = 0.715;
  const lumB = 0.072;

  Multiply([
    lumR + cos * (1 - lumR) + sin * (-lumR), lumG + cos * (-lumG) + sin * (-lumG), lumB + cos * (-lumB) + sin * (1 - lumB), 0, 0,
    lumR + cos * (-lumR) + sin * (0.143), lumG + cos * (1 - lumG) + sin * (0.140), lumB + cos * (-lumB) + sin * (-0.283), 0, 0,
    lumR + cos * (-lumR) + sin * (-(1 - lumR)), lumG + cos * (-lumG) + sin * (lumG), lumB + cos * (1 - lumB) + sin * (lumB), 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

// TODO: define value limitations
// ! And impose in wim-program
export function Contrast(base: ColourMatrixTuple, value: number) {
  const v = value + 1;
  const o = -0.5 * (v - 1);

  Multiply([
    v, 0, 0, 0, o,
    0, v, 0, 0, o,
    0, 0, v, 0, o,
    0, 0, 0, 1, 0
  ], base);
}

// TODO: define value limitations
// ! And impose in wim-program
export function DayNight(base: ColourMatrixTuple, value: number) {
  Multiply([
    value * (-2.0), -value, 0, 0, 0,
    -value, 0, value, 0, 0,
    0, value, value * 2.0, 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

export function BlackWhite(base: ColourMatrixTuple) {
  Multiply([
    0.3, 0.6, 0.1, 0, 0,
    0.3, 0.6, 0.1, 0, 0,
    0.3, 0.6, 0.1, 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

export function Negative(base: ColourMatrixTuple) {
  Multiply([
    -1, 0, 0, 1, 0,
    0, -1, 0, 1, 0,
    0, 0, -1, 1, 0,
    0, 0, 0, 1, 0
  ], base);
}

export function DesaturateLuminance(base: ColourMatrixTuple) {
  Multiply([
    0.2764723, 0.9297080, 0.0938197, 0, -37.1,
    0.2764723, 0.9297080, 0.0938197, 0, -37.1,
    0.2764723, 0.9297080, 0.0938197, 0, -37.1,
    0, 0, 0, 1, 0
  ], base);
}

export function Sepia(base: ColourMatrixTuple) {
  Multiply([
    0.393, 0.7689999, 0.18899999, 0, 0,
    0.349, 0.6859999, 0.16799999, 0, 0,
    0.272, 0.5339999, 0.13099999, 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

export function LSD(base: ColourMatrixTuple) {
  Multiply([
    2, -0.4, 0.5, 0, 0,
    -0.5, 2, -0.4, 0, 0,
    -0.4, -0.5, 3, 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

export function Brown(base: ColourMatrixTuple) {
  Multiply([
    0.5997023498159715, 0.34553243048391263, -0.2708298674538042, 0, 47.43192855600873,
    -0.037703249837783157, 0.8609577587992641, 0.15059552388459913, 0, -36.96841498319127,
    0.24113635128153335, -0.07441037908422492, 0.44972182064877153, 0, -7.562075277591283,
    0, 0, 0, 1, 0
  ], base);
}

export function VintagePinhole(base: ColourMatrixTuple) {
  Multiply([
    0.6279345635605994, 0.3202183420819367, -0.03965408211312453, 0, 9.651285835294123,
    0.02578397704808868, 0.6441188644374771, 0.03259127616149294, 0, 7.462829176470591,
    0.0466055556782719, -0.0851232987247891, 0.5241648018700465, 0, 5.159190588235296,
    0, 0, 0, 1, 0
  ], base);
}

export function Kodachrome(base: ColourMatrixTuple) {
  Multiply([
    1.1285582396593525, -0.3967382283601348, -0.03992559172921793, 0, 63.72958762196502,
    -0.16404339962244616, 1.0835251566291304, -0.05498805115633132, 0, 24.732407896706203,
    -0.16786010706155763, -0.5603416277695248, 1.6014850761964943, 0, 35.62982807460946,
    0, 0, 0, 1, 0
  ], base);
}

export function Technicolor(base: ColourMatrixTuple) {
  Multiply([
    1.9125277891456083, -0.8545344976951645, -0.09155508482755585, 0, 11.793603434377337,
    -0.3087833385928097, 1.7658908555458428, -0.10601743074722245, 0, -70.35205161461398,
    -0.231103377548616, -0.7501899197440212, 1.847597816108189, 0, 30.950940869491138,
    0, 0, 0, 1, 0
  ], base);
}

export function Polaroid(base: ColourMatrixTuple) {
  Multiply([
    1.438, -0.062, -0.062, 0, 0,
    -0.122, 1.378, -0.122, 0, 0,
    -0.016, -0.016, 1.483, 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

export function ShiftToBGR(base: ColourMatrixTuple) {
  Multiply([
    0, 0, 1, 0, 0,
    0, 1, 0, 0, 0,
    1, 0, 0, 0, 0,
    0, 0, 0, 1, 0
  ], base);
}

/**
 * Applies a colour-matrix transform in-place on `base`. Called with `entry.value` from {@link ColourMatrixPropUsage} (number or boolean).
 */
export type ColourMatrixPropCallFn = (base: ColourMatrixTuple, value: number | boolean) => void;

/**
 * TODO: Leave notes
 */

export const colourMatrixPropCalls = {
  [ColourMatrixProp.brightness]: Brightness,
  [ColourMatrixProp.saturation]: Saturation,
  [ColourMatrixProp.desaturation]: Desaturation,
  [ColourMatrixProp.hueShift]: HueShift,
  [ColourMatrixProp.contrast]: Contrast,
  [ColourMatrixProp.dayNight]: DayNight,
  [ColourMatrixProp.blackWhite]: BlackWhite,
  [ColourMatrixProp.negative]: Negative,
  [ColourMatrixProp.desaturateLuminance]: DesaturateLuminance,
  [ColourMatrixProp.sepia]: Sepia,
  [ColourMatrixProp.lsd]: LSD,
  [ColourMatrixProp.brown]: Brown,
  [ColourMatrixProp.vintagePinhole]: VintagePinhole,
  [ColourMatrixProp.kodachrome]: Kodachrome,
  [ColourMatrixProp.technicolor]: Technicolor,
  [ColourMatrixProp.polaroid]: Polaroid,
  [ColourMatrixProp.BGR]: ShiftToBGR
} as Record<ColourMatrixProp, ColourMatrixPropCallFn>;
